import React, { useState } from 'react';
import { MessageCircle, Package, CheckCircle, Clock, Zap, Calendar, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { apiService } from '@/services/api';
import { generateWhatsAppLink, trackWhatsAppClick } from '@/utils/whatsappTemplates';
import { siteContent } from '@/data/siteContent';
import { CONTACT_INFO, SERVICE_TIERS } from '../data/mockData';

const { global } = siteContent;

const Deliveries = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleWhatsAppClick = (templateName, variables = {}) => {
    trackWhatsAppClick({ templateName, page: 'deliveries' });
    const link = generateWhatsAppLink(templateName, variables);
    window.open(link, '_blank');
  };

  const useCases = [
    { title: 'Store Pickups', description: 'Collect online purchases from local stores' },
    { title: 'Document Delivery', description: 'Important paperwork delivered same-day' },
    { title: 'Small Boxes', description: 'Up to 5 boxes or small packages' },
    { title: 'Marketplace Items', description: 'Facebook Marketplace, Gumtree pickups' }
  ];

  const included = [
    'Real-time WhatsApp updates',
    'Photo proof-of-delivery',
    'Secure handling',
    'Door-to-door service'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formData = new FormData(e.target);
      const data = Object.fromEntries(formData.entries());
      
      const response = await apiService.submitDeliveryQuote(data);
      toast.success(`Quote submitted! Reference: ${response.quote_id}`);
      
      // Open WhatsApp with form data
      setTimeout(() => {
        handleWhatsAppClick('delivery', { ...data, refId: response.quote_id });
      }, 1500);
      
      e.target.reset();
    } catch (error) {
      toast.error('Failed to submit. Please try WhatsApp instead.');
      handleWhatsAppClick('delivery', {});
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#001A47] via-[#002B6B] to-[#001A47] text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Package className="h-16 w-16 text-[#039fff] mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Local Deliveries</h1>
            <p className="text-xl text-gray-300 mb-8">
              Fast, reliable delivery service across Stellenbosch & Cape Town
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-[#fb6402] hover:bg-[#e55a02] text-white px-8"
                onClick={() => handleWhatsAppClick('delivery', {})}
                data-testid="delivery-whatsapp-btn"
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                {global.primaryCTA}
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white hover:text-[#001A47]"
                onClick={() => document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Get a Quote
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Service Tiers */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#001A47] text-center mb-12">Choose Your Speed</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {SERVICE_TIERS.map((tier) => {
              const Icon = { Zap, Clock, Calendar }[tier.icon];
              return (
                <Card key={tier.id} className="hover:shadow-xl transition-all border-2 hover:border-[#039fff]">
                  <CardContent className="p-6">
                    <Icon className="h-12 w-12 text-[#fb6402] mb-4" />
                    <h3 className="text-xl font-bold text-[#001A47] mb-2">{tier.name}</h3>
                    <p className="text-gray-700 mb-2">{tier.description}</p>
                    <p className="text-sm text-[#039fff] font-semibold">{tier.tagline}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#001A47] text-center mb-12">Perfect For</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {useCases.map((useCase, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="bg-[#039fff]/10 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                    <Package className="h-6 w-6 text-[#039fff]" />
                  </div>
                  <h3 className="font-bold text-[#001A47] mb-2">{useCase.title}</h3>
                  <p className="text-sm text-gray-600">{useCase.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-[#001A47] text-center mb-12">What's Included</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {included.map((item, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-[#039fff] flex-shrink-0 mt-1" />
                  <p className="text-lg text-gray-700">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section id="quote" className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-[#001A47] mb-4">Get Your Delivery Quote</h2>
              <p className="text-gray-600">Fill in your details and we'll respond in minutes</p>
            </div>

            <Card>
              <CardContent className="p-6 md:p-8">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="pickup">Pickup Address</Label>
                      <Input id="pickup" placeholder="e.g., Stellenbosch CBD" required />
                    </div>
                    <div>
                      <Label htmlFor="dropoff">Drop-off Address</Label>
                      <Input id="dropoff" placeholder="e.g., Somerset West" required />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="item">Item Description</Label>
                    <Input id="item" placeholder="e.g., 3 boxes, documents" required />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="datetime">Preferred Date/Time</Label>
                      <Input id="datetime" type="datetime-local" required />
                    </div>
                    <div>
                      <Label htmlFor="tier">Service Tier</Label>
                      <select id="tier" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                        <option>Rocket Van NOW</option>
                        <option>Rocket Van TODAY</option>
                        <option>Rocket Van TOMORROW</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Your Name</Label>
                      <Input id="name" placeholder="Full name" required />
                    </div>
                    <div>
                      <Label htmlFor="mobile">Mobile Number</Label>
                      <Input id="mobile" type="tel" placeholder="+27" required />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="notes">Special Instructions</Label>
                    <Textarea id="notes" placeholder="Stairs, access codes, etc." rows={3} />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button type="submit" className="flex-1 bg-[#fb6402] hover:bg-[#e55a02]">
                      Submit Quote Request
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => handleWhatsAppClick('delivery', {})}
                    >
                      <MessageCircle className="mr-2 h-4 w-4" />
                      WhatsApp Instead
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-[#001A47] to-[#002B6B] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Need Regular Deliveries?</h2>
          <p className="text-xl text-gray-300 mb-8">Check out our Business Accounts for better rates</p>
          <Button size="lg" className="bg-[#fb6402] hover:bg-[#e55a02]" onClick={() => window.location.href = '/business'}>
            Learn More
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Deliveries;
